/**
 * SA-1329 (spec §4): the panel's bulk wiring: which blocks can be selected, the
 * selection, the inline confirm and the run. The panel hands in the candidates
 * its rows make and draws the bar and the blocks from what comes back.
 */
import { useEffect, useMemo, useState } from 'react'
import { startTriageRun } from '@/services/triageRun'
import { useInboxStore } from '@/stores/inboxStore'
import type { InboxTriage, TriageRunItem } from '@/types/inbox'
import type { TriageSelectionBarProps } from './TriageSelectionBar'
import { bulkVerdict, changeKindChoices, confirmText, type BulkCandidate } from './triageBulk'
import { approveN, BULK_KIND_EXCLUDED, RUN_BUSY } from './triageText'
import { useTriageSelection } from './useTriageSelection'

interface Args {
  conversationId: string
  triage: InboxTriage
  cands: readonly BulkCandidate[]
  noun: string
  /** The outcome each block's select holds, by ledger id ('' is "Do not close"). */
  outcomePicks: Readonly<Record<string, string>>
}

/** A closing kind's candidate follows the block's own outcome pick, as single-row Approve does. */
function withPick(c: BulkCandidate, triage: InboxTriage, picks: Readonly<Record<string, string>>): BulkCandidate {
  const pick = picks[c.key]
  const closing = !!triage.kinds.find((kind) => kind.id === c.kindId)?.outcomes?.length
  if (pick === undefined || !closing || (c.state !== 'approvable' && c.state !== 'needs_outcome')) return c
  const outcome = pick || null
  return { ...c, outcome, state: outcome ? 'approvable' : 'needs_outcome' }
}

export function usePanelBulk({ conversationId, triage, cands: raw, noun, outcomePicks }: Args) {
  const cands = raw.map((c) => withPick(c, triage, outcomePicks))
  const keysKey = cands.map((c) => c.key).join('|')
  const keys = useMemo(() => (keysKey ? keysKey.split('|') : []), [keysKey])
  const sel = useTriageSelection(keys, conversationId)
  const run = useInboxStore((s) => s.triageRun)
  const [confirming, setConfirming] = useState(false)
  const [busyFor, setBusyFor] = useState<string | null>(null)

  const picked = cands.filter((c) => sel.isSelected(c.key))
  const pickKey = picked.map((c) => c.key).join('|')
  const running = run?.status === 'running'
  const runItems = run ? run.items.filter((item) => item.conversationId === conversationId) : []
  const runHere = runItems.length > 0
  // A new selection after a finished run takes the bar back from the run's summary.
  const staleDone = run?.status === 'done' && runHere && picked.length > 0
  const barRun = run && !staleDone && (running || runHere) ? run : null
  useEffect(() => {
    if (staleDone) useInboxStore.getState().clearTriageRun()
  }, [staleDone])
  // A run going anywhere keeps the bar up (spec 9.7), so the disabled boxes have the run's reason beside them.
  const barOn = picked.length > 0 || runHere || running

  const start = (action: 'approve' | 'change' | 'dismiss' | 'rerun', kind?: string) => {
    const id = startTriageRun({
      action,
      origin: 'panel',
      kind,
      items: picked.map((c) => ({
        key: c.key,
        conversationId: c.conversationId,
        ledgerId: c.ledgerId as string,
        mailboxId: c.mailboxId,
        label: c.label,
        ...(action === 'approve' && c.outcome ? { outcome: c.outcome } : {}),
      })),
    })
    setConfirming(false)
    if (!id) {
      // Another run holds the store: say so rather than drop the press.
      setBusyFor(pickKey)
      return
    }
    sel.clear()
  }

  const { choices, excluded } = changeKindChoices(picked.map(() => triage.kinds))
  const text = confirmText(picked)
  const done = barRun?.status === 'done'

  const barProps: TriageSelectionBarProps = {
    selected: barRun ? barRun.items.length : picked.length,
    total: Math.max(cands.length, barRun ? barRun.items.length : 0),
    allState: sel.allState,
    onToggleAll: () => sel.setAll(sel.allState !== true),
    verdict: bulkVerdict(picked, noun),
    noun,
    onApprove: () => setConfirming(true),
    onRerun: () => start('rerun'),
    onDismiss: () => start('dismiss'),
    changeKind: { choices, note: excluded ? BULK_KIND_EXCLUDED(noun) : null },
    onOpenChangeKind: () => {},
    onApplyKind: (kindId) => start('change', kindId),
    confirm: confirming && picked.length > 0
      ? { heading: text.heading, lines: text.lines, skipped: [], action: approveN(picked.length) }
      : null,
    onConfirm: () => start('approve'),
    onCancelConfirm: () => setConfirming(false),
    checking: null,
    run: barRun,
    // The sentence belongs to the selection it was said for.
    error: busyFor !== null && busyFor === pickKey ? RUN_BUSY : null,
    onClear: () => {
      setConfirming(false)
      setBusyFor(null)
      if (done) useInboxStore.getState().clearTriageRun()
      else sel.clear()
    },
  }

  /** The run's item for a block of this panel's thread, if the run holds it. */
  const itemFor = (ledgerId: string): TriageRunItem | undefined =>
    runItems.find((item) => item.ledgerId === ledgerId)

  return { sel, running, barOn, barProps, itemFor }
}
